import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { auditLogAPI } from '../../services/apiClient';
import BLoading from './ui/BLoading';
import styles from './SurveyDeletionAuditLogPage.module.css';

const PAGE_SIZE = 20;

const formatDateTime = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString();
};

const stringifyDetails = (details) => {
  if (!details) return '-';
  if (typeof details === 'string') return details;
  try {
    return JSON.stringify(details);
  } catch (_) {
    return String(details);
  }
};

const statusClassName = (statusCode) => {
  const code = Number(statusCode || 0);
  if (code >= 500) return styles.statusError;
  if (code >= 400) return styles.statusWarning;
  return styles.statusSuccess;
};

const SurveyDeletionAuditLogPage = () => {
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0, has_next: false, has_prev: false });
  const [isLoading, setIsLoading] = useState(true);

  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');

  const fetchAuditLogs = useCallback(async (page = 1) => {
    setIsLoading(true);
    try {
      const params = {
        page,
        per_page: PAGE_SIZE,
      };

      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (sourceFilter) params.source = sourceFilter;
      if (moduleFilter) params.module = moduleFilter;

      const response = await auditLogAPI.getLogs(params);
      const data = response.data || {};
      setLogs(Array.isArray(data.logs) ? data.logs : []);
      setPagination(data.pagination || { page: 1, pages: 1, total: 0, has_next: false, has_prev: false });
    } catch (error) {
      console.error('Failed to load audit logs:', error);
      toast.error(error.response?.data?.error || 'Failed to load audit logs.');
      setLogs([]);
      setPagination({ page: 1, pages: 1, total: 0, has_next: false, has_prev: false });
    } finally {
      setIsLoading(false);
    }
  }, [moduleFilter, searchTerm, sourceFilter]);

  useEffect(() => {
    fetchAuditLogs(1);
  }, [fetchAuditLogs]);

  const handleApplyFilters = (event) => {
    event.preventDefault();
    setSearchTerm(searchInput);
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setSearchTerm('');
    setSourceFilter('');
    setModuleFilter('');
  };

  return (
    <main className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={`${styles.title} page-title`}>Audit Log</h1>
          <p className={styles.subtitle}>Unified audit trail for surveys, AI actions, business management, and user administration.</p>
        </div>
        <div className={styles.countPill}>{pagination.total || 0} entries</div>
      </header>

      <section className={styles.controlsCard}>
        <form className={styles.filters} onSubmit={handleApplyFilters}>
          <div className={styles.filterField}>
            <label htmlFor="audit-search">Search</label>
            <input
              id="audit-search"
              type="text"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Action, actor, resource, endpoint"
            />
          </div>

          <div className={styles.filterField}>
            <label htmlFor="audit-source">Source</label>
            <select
              id="audit-source"
              value={sourceFilter}
              onChange={(event) => setSourceFilter(event.target.value)}
            >
              <option value="">All sources</option>
              <option value="GENERAL_ACTIVITY">General Activity</option>
              <option value="SURVEY_QUEST">Survey/Quest</option>
              <option value="SURVEY_DELETION">Survey Deletion</option>
              <option value="AI_USAGE">AI Usage</option>
            </select>
          </div>

          <div className={styles.filterField}>
            <label htmlFor="audit-module">Module</label>
            <select
              id="audit-module"
              value={moduleFilter}
              onChange={(event) => setModuleFilter(event.target.value)}
            >
              <option value="">All modules</option>
              <option value="SURVEYS">Surveys</option>
              <option value="USER_MANAGEMENT">User Management</option>
              <option value="BUSINESS_MANAGEMENT">Business Management</option>
              <option value="QUESTS">Quests</option>
              <option value="AI">AI</option>
            </select>
          </div>

          <div className={styles.filterActions}>
            <button type="submit" className={styles.primaryButton}>Apply</button>
            <button type="button" className={styles.secondaryButton} onClick={handleClearFilters}>Clear</button>
          </div>
        </form>
      </section>

      <section className={styles.tableCard}>
        {isLoading ? (
          <div className={styles.loadingState}><BLoading variant="inline" label="Loading audit logs..." /></div>
        ) : logs.length === 0 ? (
          <div className={styles.emptyState}>No audit logs found for the current filters.</div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Occurred</th>
                  <th>Source</th>
                  <th>Module</th>
                  <th>Action</th>
                  <th>Resource</th>
                  <th>Actor</th>
                  <th>Business</th>
                  <th>Status</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => {
                  const details = stringifyDetails(log.details);
                  return (
                    <tr key={log.uid}>
                      <td>{formatDateTime(log.occurred_at)}</td>
                      <td>
                        <span className={styles.sourceBadge}>{log.source || '-'}</span>
                      </td>
                      <td>{log.module || '-'}</td>
                      <td>
                        <span className={styles.actionDefault}>{log.action_type || '-'}</span>
                      </td>
                      <td>
                        <div className={styles.primaryCell}>{log.resource_title || '-'}</div>
                        <div className={styles.secondaryCell}>
                          {log.resource_type ? `${log.resource_type}${log.resource_id ? ` #${log.resource_id}` : ''}` : '-'}
                        </div>
                      </td>
                      <td>
                        <div className={styles.primaryCell}>{log.actor_name || '-'}</div>
                        <div className={styles.secondaryCell}>{log.actor_type || '-'}</div>
                      </td>
                      <td>
                        <div className={styles.primaryCell}>{log.business_name || '-'}</div>
                        <div className={styles.secondaryCell}>{log.business_id ? `ID: ${log.business_id}` : '-'}</div>
                      </td>
                      <td>
                        <span className={`${styles.statusBadge} ${statusClassName(log.response_status)}`}>
                          {log.response_status || '-'}
                        </span>
                      </td>
                      <td className={styles.notesCell} title={details}>{details}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <footer className={styles.pagination}>
        <button
          type="button"
          className={styles.secondaryButton}
          disabled={!pagination.has_prev || isLoading}
          onClick={() => fetchAuditLogs((pagination.page || 1) - 1)}
        >
          Previous
        </button>
        <span className={styles.pageMeta}>
          Page {pagination.page || 1} of {pagination.pages || 1}
        </span>
        <button
          type="button"
          className={styles.secondaryButton}
          disabled={!pagination.has_next || isLoading}
          onClick={() => fetchAuditLogs((pagination.page || 1) + 1)}
        >
          Next
        </button>
      </footer>
    </main>
  );
};

export default SurveyDeletionAuditLogPage;
